import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import BlueXLoader, { PageDataLoader } from './BlueXLoader';
import { createPortal } from 'react-dom';
import { queryClient } from '../queryClient';
import { partnersPageQueryKey } from '../queryCacheScope';

import { createPartner, deletePartner, listPartners, updatePartner } from '../services/partner.service';
import { listOrganizations } from '../services/user.service';
import {
  compareLocaleStrings,
  compareNumbers,
  getTableSortIcon,
  stableSortTableRows,
  toggleTableSortConfig,
} from '../utils/tableSort';
import { useTableColumnResize } from '../utils/tableColumnResize';
import { PARTNERS_TABLE_COLUMNS, PARTNERS_TABLE_COLUMN_WIDTHS_KEY } from '../utils/resizableTableColumns';
import { ResizableColgroup, ResizableTableHead } from './ResizableTableHead';
import { TrashIcon } from '../shared/components/TrashIcon';
import TablePagination, { TABLE_PAGE_SIZE, usePaginatedRows } from '../shared/components/TablePagination';
import '../features/assets/assets.css';
import '../features/settings/users-orgs/users-orgs.css';

const SEARCH_MAX_LEN = 500;
const PARTNERS_CACHE_MS = 3 * 60 * 1000;

function sanitizeSearchInput(raw) {
  return String(raw ?? '')
    .replace(/\0/g, '')
    .slice(0, SEARCH_MAX_LEN);
}

const EMPTY_FORM = {
  id: null,
  name: '',
  email: '',
  referral_code: '',
  tier: 'bronze',
  commission_rate: '10.0',
  tier_locked: false,
  organization_ids: [],
};

/** Standard commission for each tier; admins can still type a custom rate. */
const TIER_DEFAULT_RATES = { bronze: 10, silver: 20, gold: 30 };

function coerceRate(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.max(0, Math.min(100, numeric));
}

function formatCommissionRateString(value) {
  return String(coerceRate(value).toFixed(1));
}

function normalizeForm(partner) {
  if (!partner || typeof partner !== 'object') return { ...EMPTY_FORM };
  let organization_ids = [];
  if (Array.isArray(partner.organization_ids)) {
    organization_ids = partner.organization_ids.map((value) => Number(value)).filter((value) => Number.isFinite(value));
  } else if (Array.isArray(partner.organizations)) {
    organization_ids = partner.organizations
      .map((org) => Number(org?.id ?? org?.organization_id))
      .filter((value) => Number.isFinite(value));
  }
  return {
    id: partner.id ?? null,
    name: partner.name ?? '',
    email: partner.email ?? '',
    referral_code: partner.referral_code ?? '',
    tier: String(partner.tier || 'bronze').toLowerCase(),
    commission_rate: formatCommissionRateString(partner.commission_rate ?? 10),
    tier_locked: partner.tier_locked === true,
    organization_ids,
  };
}

function toggleIdInList(values, id) {
  const numeric = Number(id);
  if (!Number.isFinite(numeric)) return values;
  return values.includes(numeric)
    ? values.filter((value) => value !== numeric)
    : [...values, numeric];
}

function partnerSearchBlob(partner) {
  const orgNames = Array.isArray(partner?.organizations)
    ? partner.organizations.map((o) => o?.name).filter(Boolean)
    : [];
  return [
    partner?.name,
    partner?.email,
    partner?.referral_code,
    partner?.tier,
    String(partner?.commission_rate ?? ''),
    String(partner?.total_clients ?? ''),
    ...orgNames,
  ]
    .join(' ')
    .toLowerCase();
}

const TIER_FILTER_OPTIONS = [
  { value: '', label: 'All tiers' },
  { value: 'bronze', label: 'Bronze' },
  { value: 'silver', label: 'Silver' },
  { value: 'gold', label: 'Gold' },
];

const CLIENTS_FILTER_OPTIONS = [
  { value: '', label: 'All clients' },
  { value: 'with', label: 'With clients' },
  { value: 'without', label: 'No clients' },
];

const FORM_TIER_OPTIONS = [
  { value: 'bronze', label: 'Bronze' },
  { value: 'silver', label: 'Silver' },
  { value: 'gold', label: 'Gold' },
];

export default function PartnerManagementSettingsPage() {
  const searchId = useId();
  const tierRef = useRef(null);
  const clientsRef = useRef(null);
  const formTierRef = useRef(null);
  const [partners, setPartners] = useState(
    () => queryClient.getQueryData(partnersPageQueryKey())?.partners || []
  );
  const [partnersHeaderActionsHost, setPartnersHeaderActionsHost] = useState(null);

  useLayoutEffect(() => {
    setPartnersHeaderActionsHost(document.getElementById('partners-page-header-actions'));
  }, []);
  const [organizations, setOrganizations] = useState(
    () => queryClient.getQueryData(partnersPageQueryKey())?.organizations || []
  );
  const [loading, setLoading] = useState(
    () => queryClient.getQueryData(partnersPageQueryKey()) == null
  );
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(() => ({ ...EMPTY_FORM }));
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [clientsFilter, setClientsFilter] = useState('');
  const [tierOpen, setTierOpen] = useState(false);
  const [clientsOpen, setClientsOpen] = useState(false);
  const [formTierOpen, setFormTierOpen] = useState(false);
  const [commissionRateInputFocused, setCommissionRateInputFocused] = useState(false);
  const [orgAssignSearch, setOrgAssignSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const partnerOrgSearchId = useId();

  const loadData = useCallback(async ({ fresh = false } = {}) => {
    const cached = queryClient.getQueryData(partnersPageQueryKey());
    if (cached && !fresh) {
      setPartners(Array.isArray(cached.partners) ? cached.partners : []);
      setOrganizations(Array.isArray(cached.organizations) ? cached.organizations : []);
      setLoading(false);
      const updatedAt = queryClient.getQueryState(partnersPageQueryKey())?.dataUpdatedAt || 0;
      if (Date.now() - updatedAt < PARTNERS_CACHE_MS) return;
    } else if (!cached) {
      setLoading(true);
    }

    try {
      const [pRes, oRes] = await Promise.allSettled([listPartners(), listOrganizations()]);
      const nextPartners =
        pRes.status === 'fulfilled' && Array.isArray(pRes.value) ? pRes.value : [];
      const nextOrganizations =
        oRes.status === 'fulfilled' && Array.isArray(oRes.value) ? oRes.value : [];
      setPartners(nextPartners);
      setOrganizations(nextOrganizations);
      if (pRes.status === 'fulfilled' && oRes.status === 'fulfilled') {
        queryClient.setQueryData(partnersPageQueryKey(), {
          partners: nextPartners,
          organizations: nextOrganizations,
        });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const onDoc = (event) => {
      if (tierRef.current && !tierRef.current.contains(event.target)) setTierOpen(false);
      if (clientsRef.current && !clientsRef.current.contains(event.target)) setClientsOpen(false);
      if (formTierRef.current && !formTierRef.current.contains(event.target)) setFormTierOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selectedTierLabel = TIER_FILTER_OPTIONS.find((opt) => opt.value === tierFilter)?.label || 'All tiers';
  const selectedClientsLabel = CLIENTS_FILTER_OPTIONS.find((opt) => opt.value === clientsFilter)?.label || 'All clients';

  const filteredPartners = useMemo(() => {
    let rows = partners;
    const tier = String(tierFilter || '').trim().toLowerCase();
    if (tier) {
      rows = rows.filter((p) => String(p.tier || 'bronze').toLowerCase() === tier);
    }
    if (clientsFilter === 'with') {
      rows = rows.filter((p) => Number(p.total_clients || 0) > 0);
    } else if (clientsFilter === 'without') {
      rows = rows.filter((p) => Number(p.total_clients || 0) === 0);
    }
    const q = String(searchQuery || '').trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((partner) => partnerSearchBlob(partner).includes(q));
  }, [partners, searchQuery, tierFilter, clientsFilter]);

  const handlePartnerSort = useCallback((key) => {
    setSortConfig((prev) => toggleTableSortConfig(prev, key));
  }, []);

  const getPartnerSortIcon = useCallback((key) => getTableSortIcon(sortConfig, key), [sortConfig]);
  const {
    widths: partnerColumnWidths,
    onResizeStart: onPartnerColumnResizeStart,
    tableMinWidth: partnersTableMinWidth,
    resizingKey: partnerColumnResizingKey,
  } = useTableColumnResize(PARTNERS_TABLE_COLUMNS, PARTNERS_TABLE_COLUMN_WIDTHS_KEY);

  const sortedPartners = useMemo(
    () =>
      stableSortTableRows(filteredPartners, sortConfig, (a, b, key) => {
        if (key === 'name') return compareLocaleStrings(a.name, b.name);
        if (key === 'email') return compareLocaleStrings(a.email, b.email);
        if (key === 'referral') return compareLocaleStrings(a.referral_code, b.referral_code);
        if (key === 'tier') return compareLocaleStrings(a.tier, b.tier);
        if (key === 'commission') return compareNumbers(a.commission_rate, b.commission_rate);
        if (key === 'clients') return compareNumbers(a.total_clients, b.total_clients);
        return 0;
      }),
    [filteredPartners, sortConfig]
  );
  const partnersPagination = usePaginatedRows(sortedPartners, TABLE_PAGE_SIZE);

  const visiblePartnerOrganizations = useMemo(() => {
    const q = String(orgAssignSearch || '').trim().toLowerCase();
    if (!q) return organizations;
    return organizations.filter((org) => {
      const blob = [org?.name, org?.uu_id, String(org?.id ?? '')]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return blob.includes(q);
    });
  }, [organizations, orgAssignSearch]);
  const visiblePartners = partnersPagination.pageRows;

  const partnersTableHeaderRef = useRef(null);
  const partnersTableScrollRef = useRef(null);
  const [partnersHeaderGutterPx, setPartnersHeaderGutterPx] = useState(8);

  const handlePartnersTableScroll = useCallback((event) => {
    if (partnersTableHeaderRef.current) {
      partnersTableHeaderRef.current.scrollLeft = event.currentTarget.scrollLeft;
    }
  }, []);

  useLayoutEffect(() => {
    const el = partnersTableScrollRef.current;
    if (!el) return undefined;
    const CONTENT_INSET_PX = 16;
    const syncGutter = () => {
      const scrollbarLane = Math.max(0, Math.round(el.offsetWidth - el.clientWidth));
      const next = scrollbarLane + CONTENT_INSET_PX;
      setPartnersHeaderGutterPx((prev) => (prev === next ? prev : next));
    };
    syncGutter();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncGutter) : null;
    ro?.observe(el);
    window.addEventListener('resize', syncGutter);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', syncGutter);
    };
  }, [loading, sortedPartners.length]);

  const partnersTableClassName =
    'assets-table assets-inventory-table assets-inventory-table--resizable risks-page-table partners-settings-table';
  const partnersTableStyle = { width: '100%', minWidth: partnersTableMinWidth };

  const openCreateModal = () => {
    setFormTierOpen(false);
    setCommissionRateInputFocused(false);
    setOrgAssignSearch('');
    setForm({ ...EMPTY_FORM });
    setModalOpen(true);
  };

  const openEditModal = (partner) => {
    setFormTierOpen(false);
    setCommissionRateInputFocused(false);
    setOrgAssignSearch('');
    setForm(normalizeForm(partner));
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setFormTierOpen(false);
    setCommissionRateInputFocused(false);
    setOrgAssignSearch('');
    setForm({ ...EMPTY_FORM });
  };

  const commissionNumberDisplayedValue = commissionRateInputFocused
    ? form.commission_rate
    : formatCommissionRateString(form.commission_rate);

  const selectedFormTierLabel =
    FORM_TIER_OPTIONS.find((opt) => opt.value === String(form.tier || '').toLowerCase())?.label || 'Bronze';

  const handleSave = async () => {
    const name = String(form.name || '').trim();
    const email = String(form.email || '').trim();
    if (!name || !email) {
      window.alert('Partner name and email are required.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        id: form.id,
        name,
        email,
        referral_code: String(form.referral_code || '').trim(),
        tier: String(form.tier || 'bronze').toLowerCase(),
        commission_rate: coerceRate(form.commission_rate),
        tier_locked: form.tier_locked === true,
        organization_ids: form.organization_ids,
      };

      if (form.id) {
        await updatePartner(payload);
      } else {
        await createPartner(payload);
      }

      await loadData({ fresh: true });
      closeModal();
    } catch (err) {
      window.alert(err?.message || 'Failed to save partner.');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setSaving(true);
    try {
      await deletePartner({ id: deleteTarget.id });
      setDeleteTarget(null);
      await loadData({ fresh: true });
    } catch (err) {
      window.alert(err?.message || 'Failed to delete partner.');
    } finally {
      setSaving(false);
    }
  };

  const hasActiveFilters = Boolean(
    searchQuery.trim() || String(tierFilter || '').trim() || String(clientsFilter || '').trim()
  );

  const emptyMessage = searchQuery.trim()
    ? 'No partners match your search.'
    : hasActiveFilters
      ? 'No partners match these filters.'
      : 'No partners yet. Create the first one to start assigning client organizations.';

  if (loading) {
    return (
      <section className="assets-section partners-settings-page" aria-labelledby="partners-settings-heading">
        <PageDataLoader label="Loading partners" />
      </section>
    );
  }

  return (
    <section className="assets-section partners-settings-page" aria-labelledby="partners-settings-heading">
      <h2 id="partners-settings-heading" className="edi-sr-only">
        Partners
      </h2>

      <div className="connectors-settings-table-stack">
        <div className="scans-network-split-table-stack connectors-settings-search-table-block">
          <div className="manage-users-filters-row partners-settings-filters-row">
            <div className="risks-page-filters">
              {partnersHeaderActionsHost
                ? createPortal(
                    <button
                      type="button"
                      data-tour="settings-partners-add"
                      className="users-orgs-users-action-btn users-orgs-users-action-btn--primary users-orgs-users-action-btn--compact users-orgs-top-header-manage-btn page-header-cta"
                      onClick={openCreateModal}
                    >
                      <svg className="page-header-cta-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
                        <path d="M12 5v14M5 12h14" />
                      </svg>
                      Add partner
                    </button>,
                    partnersHeaderActionsHost
                  )
                : null}
              <div className="users-orgs-users-company-filter users-orgs-role-filter-field" ref={tierRef}>
                <div className="users-orgs-users-company-filter-bar">
                  <button
                    type="button"
                    className={`users-orgs-users-company-filter-main${!tierFilter ? ' users-orgs-users-company-filter-main--all-orgs' : ''}`}
                    aria-expanded={tierOpen}
                    aria-haspopup="listbox"
                    onClick={() => setTierOpen((o) => !o)}
                  >
                    <svg
                      className="users-orgs-users-company-filter-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                    </svg>
                    <span className="users-orgs-users-company-filter-label">{selectedTierLabel}</span>
                    <span className="users-orgs-users-company-filter-chevron" aria-hidden="true" />
                  </button>
                  {tierFilter ? (
                    <button
                      type="button"
                      className="users-orgs-users-company-filter-clear"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setTierFilter('');
                        setTierOpen(false);
                      }}
                      aria-label="Clear tier filter"
                      title="Clear tier filter"
                    >
                      <svg
                        width={14}
                        height={14}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M18 6L6 18M6 6l12 12" />
                      </svg>
                    </button>
                  ) : null}
                </div>
                {tierOpen ? (
                  <div className="users-orgs-users-company-filter-panel" role="presentation">
                    <ul className="users-orgs-users-company-filter-list" role="listbox">
                      {TIER_FILTER_OPTIONS.map((opt) => (
                        <li key={`partner-tier-${opt.value || 'all'}`} role="presentation">
                          <button
                            type="button"
                            role="option"
                            aria-selected={tierFilter === opt.value}
                            className={`users-orgs-users-company-filter-option${tierFilter === opt.value ? ' is-selected' : ''}`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              setTierFilter(opt.value);
                              setTierOpen(false);
                            }}
                          >
                            {opt.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
              <div className="users-orgs-users-company-filter users-orgs-role-filter-field" ref={clientsRef}>
                <div className="users-orgs-users-company-filter-bar">
                  <button
                    type="button"
                    className={`users-orgs-users-company-filter-main${!clientsFilter ? ' users-orgs-users-company-filter-main--all-orgs' : ''}`}
                    aria-expanded={clientsOpen}
                    aria-haspopup="listbox"
                    onClick={() => setClientsOpen((o) => !o)}
                  >
                    <svg
                      className="users-orgs-users-company-filter-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                    </svg>
                    <span className="users-orgs-users-company-filter-label">{selectedClientsLabel}</span>
                    <span className="users-orgs-users-company-filter-chevron" aria-hidden="true" />
                  </button>
                  {clientsFilter ? (
                    <button
                      type="button"
                      className="users-orgs-users-company-filter-clear"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setClientsFilter('');
                        setClientsOpen(false);
                      }}
                      aria-label="Clear clients filter"
                      title="Clear clients filter"
                    >
                      <svg
                        width={14}
                        height={14}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M18 6L6 18M6 6l12 12" />
                      </svg>
                    </button>
                  ) : null}
                </div>
                {clientsOpen ? (
                  <div className="users-orgs-users-company-filter-panel" role="presentation">
                    <ul className="users-orgs-users-company-filter-list" role="listbox">
                      {CLIENTS_FILTER_OPTIONS.map((opt) => (
                        <li key={`partner-clients-${opt.value || 'all'}`} role="presentation">
                          <button
                            type="button"
                            role="option"
                            aria-selected={clientsFilter === opt.value}
                            className={`users-orgs-users-company-filter-option${clientsFilter === opt.value ? ' is-selected' : ''}`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              setClientsFilter(opt.value);
                              setClientsOpen(false);
                            }}
                          >
                            {opt.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
              <div className="subscription-org-management-search manage-users-inline-search">
                <label htmlFor={searchId} className="edi-sr-only">
                  Search partners
                </label>
                <div className="subscription-org-management-search-inner">
                  <input
                    id={searchId}
                    type="search"
                    className="subscription-org-management-search-input"
                    placeholder="Search partners"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(sanitizeSearchInput(e.target.value))}
                    autoComplete="off"
                    spellCheck={false}
                    maxLength={SEARCH_MAX_LEN}
                  />
                  {searchQuery ? (
                    <button
                      type="button"
                      className="ghost-exposure-sys-table-search-clear"
                      onClick={() => setSearchQuery('')}
                      aria-label="Clear partner search"
                    >
                      ✕
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          <div className="assets-table-shell assets-table-shell--inventory-body partners-settings-table-shell" data-tour="settings-partners-table">
            <div
              className="risks-page-table-header-frame"
              style={{ '--risks-header-gutter': `${partnersHeaderGutterPx}px` }}
            >
              <div ref={partnersTableHeaderRef} className="risks-page-table-header">
                <table className={`${partnersTableClassName} risks-page-table--header`} style={partnersTableStyle}>
                  <ResizableColgroup columns={PARTNERS_TABLE_COLUMNS} widths={partnerColumnWidths} />
                  <ResizableTableHead
                    columns={PARTNERS_TABLE_COLUMNS}
                    widths={partnerColumnWidths}
                    onResizeStart={onPartnerColumnResizeStart}
                    resizingKey={partnerColumnResizingKey}
                    sortConfig={sortConfig}
                    onSort={handlePartnerSort}
                    getSortIcon={getPartnerSortIcon}
                    resizeHandleClassName="risks-page-col-resize-handle"
                  />
                </table>
              </div>
              <div className="risks-page-table-header-gutter" aria-hidden="true" />
            </div>
            <div
              ref={partnersTableScrollRef}
              className="assets-inventory-table-scroll assets-inventory-table-scroll--body risks-page-table-scroll"
              onScroll={handlePartnersTableScroll}
            >
              <table className={`${partnersTableClassName} risks-page-table--body`} style={partnersTableStyle}>
                <ResizableColgroup columns={PARTNERS_TABLE_COLUMNS} widths={partnerColumnWidths} />
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="assets-empty-cell">
                        <BlueXLoader size="small" label="Loading partners" />
                      </td>
                    </tr>
                  ) : sortedPartners.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="assets-empty-cell">
                        {emptyMessage}
                      </td>
                    </tr>
                  ) : (
                    visiblePartners.map((partner) => (
                      <tr key={partner.id}>
                        <td className="assets-cell-ellipsis" title={partner.name || ''}>
                          {partner.name || '—'}
                        </td>
                        <td className="assets-cell-ellipsis" title={partner.email || ''}>
                          {partner.email || '—'}
                        </td>
                        <td className="assets-cell-ellipsis partners-settings-code-cell" title={partner.referral_code || 'Auto-generated'}>
                          {partner.referral_code || 'Auto-generated'}
                        </td>
                        <td>
                          <span
                            className={`partners-settings-tier-pill partners-settings-tier-pill--${String(
                              partner.tier || 'bronze'
                            ).toLowerCase()}`}
                          >
                            {String(partner.tier || 'bronze').toUpperCase()}
                          </span>
                        </td>
                        <td>{Number(partner.commission_rate || 0).toFixed(1)}%</td>
                        <td title={(partner.organizations || []).map((org) => org?.name).filter(Boolean).join(', ') || 'No organizations assigned'}>
                          {partner.total_clients || 0}
                        </td>
                        <td className="partners-settings-td-actions">
                          <div className="users-orgs-actions-cell partners-settings-actions-cell">
                            <button
                              type="button"
                              className="assets-more-btn"
                              title={`Edit ${partner.name}`}
                              aria-label={`Edit ${partner.name}`}
                              onClick={() => openEditModal(partner)}
                            >
                              <svg
                                className="assets-more-btn-icon"
                                viewBox="0 0 20 20"
                                width="14"
                                height="14"
                                fill="currentColor"
                                aria-hidden="true"
                              >
                                <circle cx="4" cy="10" r="1.6" />
                                <circle cx="10" cy="10" r="1.6" />
                                <circle cx="16" cy="10" r="1.6" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              className="connectors-settings-row-action connectors-settings-row-action--danger connectors-settings-row-action--icon-only"
                              title={`Delete ${partner.name}`}
                              aria-label={`Delete ${partner.name}`}
                              onClick={() => setDeleteTarget(partner)}
                            >
                              <TrashIcon className="connectors-settings-delete-icon" size={14} strokeWidth={2.35} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <TablePagination {...partnersPagination} itemLabel="partners" />
        </div>
      </div>

      {modalOpen ? (
        <div
          className="assets-detail-modal-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeModal();
          }}
        >
          <div
            className="assets-detail-modal partners-settings-editor-modal"
            role="dialog"
            aria-modal="true"
            aria-label={form.id ? 'Edit partner' : 'Create partner'}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="assets-detail-modal-inner">
              <div className="assets-detail-modal-header partners-settings-modal-header">
                <div className="assets-detail-modal-title" id="partners-settings-editor-title">
                  {form.id ? 'Edit partner' : 'Create partner'}
                </div>
                <div className="assets-detail-modal-actions">
                  <button type="button" className="assets-detail-modal-close" onClick={closeModal} aria-label="Close">
                    ✕
                  </button>
                </div>
              </div>

              <div className="partners-settings-editor-scroll">
              <section className="users-orgs-add-invite-card" aria-label="Partner details">
                <div className="users-orgs-add-invite-card-body users-orgs-user-detail-form partners-settings-form-grid">
                <div className="users-orgs-user-detail-field">
                  <label htmlFor="partner-form-name">Partner name</label>
                  <input
                    id="partner-form-name"
                    className="users-orgs-user-detail-input"
                    value={form.name}
                    onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))}
                    autoComplete="organization"
                  />
                </div>
                <div className="users-orgs-user-detail-field">
                  <label htmlFor="partner-form-email">Email</label>
                  <input
                    id="partner-form-email"
                    type="email"
                    className="users-orgs-user-detail-input"
                    value={form.email}
                    onChange={(e) => setForm((current) => ({ ...current, email: e.target.value }))}
                    autoComplete="email"
                  />
                </div>
                <div className="users-orgs-user-detail-field">
                  <label htmlFor="partner-form-referral">Referral code</label>
                  <input
                    id="partner-form-referral"
                    className="users-orgs-user-detail-input"
                    value={form.referral_code}
                    placeholder="Leave blank to auto-generate"
                    onChange={(e) => setForm((current) => ({ ...current, referral_code: e.target.value }))}
                  />
                </div>
                <div className="users-orgs-user-detail-field">
                  <label htmlFor="partner-form-tier">Tier</label>
                  <div className="partners-settings-tier-picker" ref={formTierRef}>
                    <button
                      type="button"
                      id="partner-form-tier"
                      className="users-orgs-user-detail-input partners-settings-tier-picker-trigger"
                      aria-expanded={formTierOpen}
                      aria-haspopup="listbox"
                      onClick={() => setFormTierOpen((o) => !o)}
                    >
                      <span className="partners-settings-tier-picker-value">{selectedFormTierLabel}</span>
                      <span className="partners-settings-tier-picker-caret" aria-hidden="true">
                        ▾
                      </span>
                    </button>
                    {formTierOpen ? (
                      <div className="partners-settings-tier-picker-menu" role="listbox" aria-labelledby="partner-form-tier">
                        {FORM_TIER_OPTIONS.map((opt) => (
                          <button
                            key={`partner-form-tier-${opt.value}`}
                            type="button"
                            role="option"
                            aria-selected={String(form.tier || '').toLowerCase() === opt.value}
                            className={`partners-settings-tier-picker-option${
                              String(form.tier || '').toLowerCase() === opt.value ? ' is-selected' : ''
                            }`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              // Choosing a tier is a manual override with that tier's standard rate.
                              setForm((current) => ({
                                ...current,
                                tier: opt.value,
                                commission_rate: formatCommissionRateString(TIER_DEFAULT_RATES[opt.value] ?? 10),
                                tier_locked: true,
                              }));
                              setFormTierOpen(false);
                            }}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="users-orgs-user-detail-field partners-settings-field-span partners-settings-commission-field">
                  <label className="partners-settings-commission-label" htmlFor="partner-form-rate-slider">
                    Commission rate (%)
                  </label>
                  <div className="partners-settings-commission-controls">
                    <div className="partners-settings-commission-controls-track">
                      <input
                        id="partner-form-rate-slider"
                        type="range"
                        className="partners-settings-commission-slider"
                        min="0"
                        max="100"
                        step="0.1"
                        value={coerceRate(form.commission_rate)}
                        style={{
                          '--commission-slider-fill': `${coerceRate(form.commission_rate)}%`,
                        }}
                        onChange={(e) =>
                          setForm((current) => ({
                            ...current,
                            commission_rate: formatCommissionRateString(e.target.value),
                            tier_locked: true,
                          }))
                        }
                      />
                      <div className="partners-settings-commission-number-wrap">
                        <input
                          id="partner-form-rate"
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          inputMode="decimal"
                          className="users-orgs-user-detail-input partners-settings-commission-number"
                          aria-label="Commission rate percent (exact value)"
                          value={commissionNumberDisplayedValue}
                          onFocus={() => setCommissionRateInputFocused(true)}
                          onBlur={() => {
                            setCommissionRateInputFocused(false);
                            setForm((current) => ({
                              ...current,
                              commission_rate: formatCommissionRateString(current.commission_rate),
                            }));
                          }}
                          onChange={(e) =>
                            setForm((current) => ({ ...current, commission_rate: e.target.value, tier_locked: true }))
                          }
                        />
                        <span className="partners-settings-commission-percent-suffix" aria-hidden="true">
                          %
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="users-orgs-user-detail-field partners-settings-field-span">
                  <label className="partners-settings-assignment-row" htmlFor="partner-form-tier-locked">
                    <input
                      id="partner-form-tier-locked"
                      type="checkbox"
                      className="users-orgs-settings-checkbox"
                      checked={form.tier_locked === true}
                      disabled={saving}
                      onChange={(e) => setForm((current) => ({ ...current, tier_locked: e.target.checked }))}
                    />
                    <span>Lock tier and commission rate (manual override)</span>
                  </label>
                  <p className="partners-settings-tier-mode-hint">
                    {form.tier_locked
                      ? 'The partner sees and is paid on this tier and rate until you unlock it.'
                      : 'Tier and rate follow the partner score automatically (Bronze 10%, Silver 20%, Gold 30%).'}
                  </p>
                </div>
                </div>
              </section>

              <section className="users-orgs-add-invite-card" aria-labelledby="partner-orgs-heading">
                <div className="users-orgs-add-invite-card-head">
                  <h3 id="partner-orgs-heading" className="users-orgs-add-invite-card-title">
                    Assigned organizations
                  </h3>
                  <p className="users-orgs-org-assign-hint">
                    Checked organizations are linked to this partner for reporting and access.
                  </p>
                </div>
                <div className="users-orgs-add-invite-card-body">
                  <input
                    id={partnerOrgSearchId}
                    type="search"
                    className="users-orgs-user-detail-input users-orgs-org-assign-search"
                    value={orgAssignSearch}
                    onChange={(e) => setOrgAssignSearch(sanitizeSearchInput(e.target.value))}
                    placeholder="Search organizations"
                    autoComplete="off"
                    aria-label="Search organizations"
                    disabled={saving}
                  />
                  <div className="partners-settings-assignment-list" role="group" aria-labelledby="partner-orgs-heading">
                    {organizations.length === 0 ? (
                      <p className="users-orgs-org-assign-empty">No organizations available.</p>
                    ) : visiblePartnerOrganizations.length === 0 ? (
                      <p className="users-orgs-org-assign-empty">No organizations match that search.</p>
                    ) : (
                      visiblePartnerOrganizations.map((org) => {
                        const checked = form.organization_ids.includes(Number(org?.id));
                        return (
                          <label key={String(org?.id ?? org?.uu_id)} className="partners-settings-assignment-row">
                            <input
                              type="checkbox"
                              className="users-orgs-settings-checkbox"
                              checked={checked}
                              disabled={saving}
                              onChange={() =>
                                setForm((current) => ({
                                  ...current,
                                  organization_ids: toggleIdInList(current.organization_ids, org?.id),
                                }))
                              }
                            />
                            <span>{org?.name || org?.uu_id || 'Organization'}</span>
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>
              </section>
              </div>

              <div className="users-orgs-user-detail-actions partners-settings-modal-actions">
                <button type="button" className="users-orgs-user-detail-save-btn" onClick={handleSave} disabled={saving}>
                  {form.id ? 'Save changes' : 'Create partner'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {deleteTarget ? (
        <div
          className="assets-detail-modal-overlay assets-exclude-confirm-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setDeleteTarget(null);
          }}
        >
          <div
            className="assets-exclude-confirm-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-label="Delete partner"
            aria-describedby="partners-delete-confirm-desc"
          >
            <button
              type="button"
              className="assets-exclude-confirm-x"
              onClick={() => setDeleteTarget(null)}
              aria-label="Close"
            >
              ✕
            </button>
            <p id="partners-delete-confirm-desc" className="assets-exclude-confirm-desc">
              You are about to permanently delete{' '}
              <strong className="assets-exclude-confirm-name">{deleteTarget.name}</strong>.
              Partner assignments are cleared from linked organizations; organizations are not removed.
              <span className="assets-exclude-confirm-warning">This action cannot be undone.</span>
            </p>
            <div className="assets-exclude-confirm-actions">
              <button
                type="button"
                className="assets-exclude-confirm-btn assets-exclude-confirm-btn--danger"
                onClick={confirmDelete}
                disabled={saving}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
